import { useEffect, useRef, useState } from 'react';

/**
 * Smoothly animate a dot's hill-x (0–100) toward a target *only* when the
 * `viewId` changes — i.e. when the user deliberately switches between
 * live and a snapshot, or between two snapshots.
 *
 * Design rationale:
 *
 * 1. JS-driven (requestAnimationFrame) instead of CSS `transition: transform`.
 *    The hill is a curve, not a line — a CSS transition on the `translate`
 *    matrix would interpolate linearly across two SVG points, so a dot
 *    moving from x=20 to x=80 would cut across the peak in a straight
 *    chord and visually leave the curve mid-animation. By interpolating
 *    `x` in hill-space and recomputing `y = hillYFromX(x)` on every
 *    frame, the dot glides *along* the curve, which is the whole point
 *    of the replay animation.
 *
 * 2. Gated by `viewId`. Three things can make `targetX` change: a live
 *    drag commit, a WebSocket echo from another client, and a user-
 *    driven view switch. Only the third should animate. The `viewId`
 *    token — opaque to this hook, produced by the caller as
 *    `selectedSnapshotId ?? 'live'` — is the signal for that intent.
 *    A stable `viewId` with a changed `targetX` snaps instantly
 *    (drag / WS land "in the live view", no animation).
 *
 * 3. Interruptible. If the user snaps view A → B → C quickly, the in-
 *    flight animation is cancelled on each switch and a new one starts
 *    from the current rendered position — no visual teleport to the
 *    start of the next animation.
 *
 * 4. `<Hill />` overrides this hook's output with the live drag position
 *    when the dot is being dragged locally (see `HillDot`). The
 *    animation can keep running internally without visible bleed —
 *    it just isn't rendered during the drag.
 *
 * Implementation: state-based view-change detection.
 * ---------------------------------------------------
 * Earlier this hook tracked the previous `viewId` with a `useRef`,
 * mutating it inside `useEffect`. That approach was theoretically sound
 * but fragile in practice: a ref mutation is invisible to React's
 * reconciler, so an interrupted render (rapid view switches, WS echo
 * landing mid-animation, React 19 concurrent replay of an aborted
 * render) could leave the ref pointing to a view that was never
 * actually committed, which made the effect fail its "view changed"
 * check and skip the animation. Symptom: the live→snapshot transition
 * animated, but snapshot→live didn't.
 *
 * The fix is to hoist the "which view is the current `renderedX`
 * committed to?" signal into React state. Comparing `viewId` to
 * `currentViewId` during render is the officially-supported
 * "derive-state-from-props" pattern (see React docs: "Adjusting state
 * when a prop changes"). Pairing that with a `pendingAnim` state slot
 * makes the animation trigger a single React-visible transition
 * instead of a hidden ref flip.
 */
export function useAnimatedHillX(targetX: number, viewId: string, duration = 300): number {
  const [renderedX, setRenderedX] = useState(targetX);
  // Tracks which view `renderedX` currently corresponds to. Kept in
  // state (not a ref) so React-visible equality checks drive the
  // animation, rather than hidden ref mutations that can be lost in
  // aborted renders or StrictMode double-invocations.
  const [committedViewId, setCommittedViewId] = useState(viewId);
  // When non-null, the effect below picks this up and starts an
  // animation from `startX` toward the current `targetX`. The effect
  // clears it back to null when the animation finishes (or when the
  // positions turn out to be equal, so no animation is needed).
  const [pendingAnim, setPendingAnim] = useState<{ startX: number } | null>(null);
  const rafRef = useRef<number | null>(null);

  // Detect a view change DURING RENDER. React officially supports
  // calling setState directly in render as long as we guard it with an
  // equality check (it schedules the change for the next render without
  // looping). This is the "adjust state when a prop changes" pattern
  // from the React docs, and it's preferred over mutating a ref in
  // useEffect because the state update is visible to React's reconciler
  // and survives StrictMode / concurrent replay correctly.
  if (viewId !== committedViewId) {
    // Capture the rendered position AT THE MOMENT OF SWITCH as the
    // animation's starting point. If a previous animation was mid-
    // flight, `renderedX` will be whatever the step function last set
    // it to, which is exactly what we want: the dot glides smoothly
    // from its visible position to the new target, no teleport.
    setCommittedViewId(viewId);
    setPendingAnim({ startX: renderedX });
  }

  useEffect(() => {
    // Cancel any animation left over from a prior switch. Runs
    // unconditionally so a stale raf can't outlive its trigger.
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    if (pendingAnim === null) {
      // No queued animation → this effect run is reacting to an in-view
      // `targetX` change (WS echo, drag commit, initial mount with
      // matching values, end-of-animation cleanup). Snap `renderedX` to
      // the new target without interpolating. React bails out when the
      // next state equals the current one (Object.is), so calling
      // setRenderedX unconditionally is safe — no redundant render, no
      // loop — and avoids needing `renderedX` in the deps array (which
      // would make the effect re-run every animation frame and break
      // the raf loop by constantly cancelling itself).
      setRenderedX(targetX);
      return;
    }

    const { startX } = pendingAnim;
    if (startX === targetX) {
      // Dot didn't move between views — clear the pending flag so the
      // next in-view update isn't mistaken for an animation and falls
      // into the snap branch above.
      setPendingAnim(null);
      return;
    }

    const startTime = performance.now();
    // easeOutQuart: fast initial motion tapering to a gentle stop.
    // Reads as "the dot swept into place" rather than a mechanical slide.
    const ease = (t: number) => 1 - (1 - t) ** 4;
    const step = (now: number) => {
      const t = Math.min(1, (now - startTime) / duration);
      const nextX = startX + (targetX - startX) * ease(t);
      setRenderedX(nextX);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        rafRef.current = null;
        // Clear the pending flag on completion so subsequent effect runs
        // (e.g. a WS echo arriving now that we're settled) take the snap
        // branch instead of re-animating.
        setPendingAnim(null);
      }
    };
    rafRef.current = requestAnimationFrame(step);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [pendingAnim, targetX, duration]);

  return renderedX;
}
